import type {
  DOMAttributes,
  FocusableDOMProps,
  FocusableElement,
  FocusableProps,
} from '@react-types/shared';
import type {MutableRefObject, ReactNode, RefObject} from 'react';
import React, {useContext, useEffect, useRef} from 'react';
import {useFocus, useKeyboard} from '../interactions';
import {mergeProps, useSyncRef} from '../utils';
import {focusSafely} from './focusSafely';

export interface FocusableOptions extends FocusableProps, FocusableDOMProps {
  /** Whether focus should be disabled. */
  isDisabled?: boolean;
}

export interface FocusableProviderProps extends DOMAttributes {
  /** The child element to provide DOM props to. */
  children?: ReactNode;
}

interface FocusableContextValue extends FocusableProviderProps {
  ref?: MutableRefObject<FocusableElement | null>;
}

const FocusableContext = React.createContext<FocusableContextValue | null>(
  null,
);

function useFocusableContext(
  ref: RefObject<FocusableElement>,
): FocusableContextValue {
  const context = useContext(FocusableContext) || {};
  useSyncRef(context, ref);

  // eslint-disable-next-line
  let {ref: _, ...otherProps} = context;
  return otherProps;
}

/**
 * Provides DOM props to the nearest focusable child.
 */
function FocusableProvider(
  props: FocusableProviderProps,
  ref: RefObject<FocusableElement>,
) {
  const {children, ...otherProps} = props;
  const context = {
    ...otherProps,
    ref,
  };

  return (
    <FocusableContext.Provider value={context}>
      {children}
    </FocusableContext.Provider>
  );
}

// @ts-expect-error - Type 'null' is not assignable to type 'RefObject<FocusableElement>'
const _FocusableProvider = React.forwardRef(FocusableProvider);
export {_FocusableProvider as FocusableProvider};

export interface FocusableAria {
  /** Props for the focusable element. */
  focusableProps: DOMAttributes;
}

/**
 * Used to make an element focusable and capable of auto focus.
 */
export function useFocusable(
  props: FocusableOptions,
  domRef: RefObject<FocusableElement>,
): FocusableAria {
  const {focusProps} = useFocus(props);
  const {keyboardProps} = useKeyboard(props);
  const interactions = mergeProps(focusProps, keyboardProps);
  const domProps = useFocusableContext(domRef);
  const interactionProps = props.isDisabled ? {} : domProps;
  const autoFocusRef = useRef(props.autoFocus);

  useEffect(() => {
    if (autoFocusRef.current && domRef.current) {
      focusSafely(domRef.current);
    }
    autoFocusRef.current = false;
  }, [domRef]);

  return {
    focusableProps: mergeProps(
      {
        ...interactions,
        tabIndex:
          props.excludeFromTabOrder && !props.isDisabled ? -1 : undefined,
      },
      interactionProps,
    ),
  };
}
